import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@nexelem/utils';
import { ButtonHTMLAttributes, FC, forwardRef, HTMLAttributeAnchorTarget, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { t } from 'i18next';
import { ThreeDotsSvg } from './ThreeDotsSvg';
import { Indicator, IndicatorProps } from '../Indicator';
import { LinkProps, To } from 'react-router-dom';

const buttonVariants = cva(
  'underline-none relative box-border inline-flex cursor-pointer items-center justify-center rounded-xs border border-transparent bg-transparent whitespace-nowrap text-text-primary-inverse transition-colors focus-visible:outline-4 focus-visible:outline-border-focus-keyboard disabled:cursor-not-allowed disabled:border disabled:border-border-disabled disabled:bg-surface-disabled disabled:text-text-disabled [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        contained: 'bg-surface-action hover:bg-surface-hover active:bg-surface-focus',
        outlined:
          'border border-border-action text-text-action hover:border-border-hover hover:bg-surface-hover-lighter hover:text-text-hover active:border-border-focus active:bg-surface-focus-lighter active:text-text-focus',
        text: 'text-text-action hover:border hover:border-border-hover-lighter hover:bg-surface-hover-lighter hover:text-text-hover active:border-border-focus-lighter active:bg-surface-focus-lighter active:text-text-focus',
        plain: 'text-text-action focus:bg-surface-focus-lighter/50 focus-visible:outline-none active:text-text-focus',
      },
      size: {
        medium:
          'h-11 gap-2 px-4 text-t16-sb [&_svg]:size-5! [&:has(.content)_svg:first-child]:-ml-1 [&:has(.content)_svg:last-child]:-mr-1',
        large:
          'h-18 gap-3 px-8 text-t20-sb [&_svg]:size-8! [&:has(.content)_svg:first-child]:-ml-3 [&:has(.content)_svg:last-child]:-mr-3',
        jumbo:
          'h-25 gap-3 px-8 text-t28-button [&_svg]:size-11! [&:has(.content)_svg:first-child]:-ml-3 [&:has(.content)_svg:last-child]:-mr-3',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'contained',
      size: 'medium',
    },
  },
);

type ButtonIndicatorProps = {
  withIndicator?: boolean;
  count?: number | string;
  countLimit?: number;
  isCountLimit?: boolean;
  status?: IndicatorProps['status'];
};

export type LinkComponentProps =
  | {
      component: FC<LinkProps>;
      to: To;
      target?: HTMLAttributeAnchorTarget;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      state?: any;
      // Cannot set disabled when button is a link
      disabled?: never;
    }
  | {
      component?: never;
      to?: never;
      target?: never;
      state?: never;
    };

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> &
  ButtonIndicatorProps &
  LinkComponentProps & {
    StartIcon?: ReactNode;
    EndIcon?: ReactNode;
    isLoading?: boolean;
    /**
     * When true, disables the full-screen overlay that appears during loading.
     * By default (false), the overlay is shown when isLoading is true.
     * @default false
     */
    disableOverlay?: boolean;
  };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      children,
      StartIcon,
      EndIcon,
      disabled,
      isLoading,
      withIndicator,
      count,
      countLimit,
      isCountLimit,
      status,
      component,
      to,
      target,
      type = 'button',
      state,
      disableOverlay = false,
      ...props
    },
    ref,
  ) => {
    const StartIconOrLoader = isLoading ? <ThreeDotsSvg className="text-icon-disabled" /> : StartIcon;
    const Text = isLoading ? t('components.button.wait') : children;
    const Comp = (component as AnyComponent) || 'button';

    const LinkToProps = component ? { to, target, state } : {};

    const showOverlay = isLoading && !disableOverlay;

    return (
      <>
        <Comp
          {...LinkToProps}
          {...props}
          className={cn(buttonVariants({ variant, size, fullWidth, className }))}
          disabled={disabled || isLoading}
          ref={ref}
          type={type}
        >
          {StartIconOrLoader}
          {Text && <span className="content">{Text}</span>}
          {withIndicator && (
            <Indicator
              className={cn('absolute', Number(count) >= 1 ? '-top-2 -right-2' : '-top-1 -right-1')}
              count={count}
              countLimit={countLimit}
              isCountLimit={isCountLimit}
              status={status}
            />
          )}
          {EndIcon}
        </Comp>
        {showOverlay &&
          createPortal(
            <div data-loading-overlay aria-hidden="true" className="fixed inset-0 z-9999 cursor-wait bg-transparent" />,
            document.body,
          )}
      </>
    );
  },
);

Button.displayName = 'Button';

// Cast to AnyComponent to match ref difference between button and link
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = FC<any>;
